import { useTranslation } from 'react-i18next'
import { styled } from 'baseui'
import { lightTheme } from '@/customTheme'
import { Z_INDEX } from '@/constants/z-index'
import { hexToRgba } from '@/utils/color'
import Icons from '@/scenes/Editor/components/Icons'
import Stack from '@/components/Stack'
import Table, { TableRow } from '@/components/Table'
import Text from '@/components/Text'
import Tooltip from '@/components/Tooltip'

/** A cell is either an included/excluded mark (`boolean`) or a literal value ("250", "1,000"). */
export type PlanComparisonValue = boolean | string

export interface PlanComparisonColumn {
  /** Key used to look the value up in each row's `values` map. */
  key: string
  label: string
  /**
   * Renders the column as a raised, accent-bordered card and turns literal
   * values into accent pills. Exactly one column is normally highlighted.
   */
  highlighted?: boolean
}

export interface PlanComparisonRow {
  label: string
  /** When set, an info icon next to the label reveals this copy on hover. */
  info?: string
  values: Record<string, PlanComparisonValue>
}

export interface PlanComparisonTableProps {
  /** Header text above the feature-label column, e.g. "What you get". */
  title: string
  columns: PlanComparisonColumn[]
  rows: PlanComparisonRow[]
  /**
   * Accent for the highlighted column's border, its card wash, its value pills
   * and every check mark. Defaults to the vivid purple.
   */
  accentColor?: string
}

// Grid metrics from Figma 5774-13654.
const LABEL_COLUMN_WIDTH = 237
const COLUMN_WIDTH = 94
const HEADER_ROW_MIN_HEIGHT = 42
const ROW_MIN_HEIGHT = 44
const PILL_WIDTH = 58
const PILL_HEIGHT = 22

const Mark = styled('span', () => ({
  display: 'flex',
}))

/**
 * Hover target for the info tooltip. A plain element on purpose: baseui's
 * tooltip clones its child to attach the mouse handlers and the ref, and a
 * component that does not forward unknown props (e.g. `Stack`) swallows them,
 * leaving an icon that never opens anything.
 */
const InfoTrigger = styled('span', () => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
}))

/**
 * Figma 5815-16202 ("Tooltip for tools"): black 8px-radius bubble, 8px inset,
 * 12px/16px centered white label. The shared `Tooltip` defaults to the app's
 * 16px popover radius, so the geometry is set here rather than changing every
 * tooltip in the app.
 *
 * The z-index is not cosmetic: this table is used inside modals, and baseui
 * renders a tooltip in its own layer. At the shared default (1100) it opens
 * *behind* a dialog stacked at Z_INDEX.MODAL and reads as "no tooltip at all".
 */
const INFO_TOOLTIP_STYLE = {
  Inner: {
    style: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '16px',
      letterSpacing: '-0.072px',
      textAlign: 'center',
      color: lightTheme.colors.white,
      padding: '8px',
      borderRadius: '8px',
    },
  },
  Body: {
    style: {
      borderRadius: '8px',
      zIndex: Z_INDEX.TOOLTIP_ABOVE_MODAL,
    },
  },
  Arrow: {
    style: {
      zIndex: Z_INDEX.TOOLTIP_ABOVE_MODAL,
    },
  },
}

/**
 * Feature-by-plan comparison table: a label column plus one column per plan,
 * with one optional highlighted (accent-framed) column.
 *
 * Formatting only — `Table` owns the grid, the dividers, the accent card and the
 * table semantics; this component decides what a plan value looks like and
 * knows nothing about plans, pricing or entitlements beyond the props it is
 * handed.
 *
 * Figma: 5774-13654 (Limited time offer).
 */
function PlanComparisonTable({
  title,
  columns,
  rows,
  accentColor = lightTheme.colors.purpleVivid,
}: PlanComparisonTableProps) {
  const { t } = useTranslation()
  const accentTint = hexToRgba(accentColor, 0.1)
  // Both the pills and the card wash derive from the accent, so a caller passing
  // a non-purple accent doesn't get a purple-tinted card under a foreign border.
  const accentSurface = hexToRgba(accentColor, 0.03)

  const renderValue = (value: PlanComparisonValue | undefined, highlighted: boolean) => {
    if (value === undefined) {
      return null
    }
    if (value === true) {
      // CheckIconSmall's glyph fills ~0.69 of its box, matching the Figma
      // check (14.2px glyph in a 20px frame). CheckIcon's fills only ~0.47.
      // The mark is the only carrier of the value, so it needs a text label of
      // its own — an icon alone reads as an empty cell to a screen reader.
      return (
        <Mark role="img" aria-label={t('Included')}>
          <Icons.CheckIconSmall width={20} height={20} fill={accentColor} />
        </Mark>
      )
    }
    if (value === false) {
      // 24 box -> 14.2px-long bars, the Figma cross glyph size.
      return (
        <Mark role="img" aria-label={t('Not included')}>
          <Icons.Close size={24} fill={lightTheme.colors.grayScale600} />
        </Mark>
      )
    }
    if (highlighted) {
      // Accent value pill. Kept as this component's own markup rather than
      // `Badge`, which is a 12px label pill — this one carries table type (14px)
      // and the row's 22px height.
      return (
        <Stack
          direction="row"
          align="center"
          justify="center"
          background={accentTint}
          width={PILL_WIDTH}
          height={PILL_HEIGHT}
          radius={100}
        >
          <Text variant="Table14medium" as="span" color={accentColor}>
            {value}
          </Text>
        </Stack>
      )
    }
    return (
      <Text variant="Table14medium" as="span" color={lightTheme.colors.grayScale600}>
        {value}
      </Text>
    )
  }

  const tableRows: TableRow[] = rows.map(row => ({
    key: row.label,
    header: (
      <>
        <Text variant="Table14regular" as="span" color={lightTheme.colors.grayScale1000}>
          {row.label}
        </Text>
        {row.info ? (
          <Tooltip
            info={{ content: row.info, placement: 'top', styleOverrides: INFO_TOOLTIP_STYLE }}
          >
            <InfoTrigger>
              {/* 22 box -> a 16px circle, the Figma info glyph size. */}
              <Icons.Info width={22} height={22} />
            </InfoTrigger>
          </Tooltip>
        ) : null}
      </>
    ),
    cells: Object.fromEntries(
      columns.map(column => [
        column.key,
        renderValue(row.values[column.key], Boolean(column.highlighted)),
      ]),
    ),
  }))

  return (
    <Table
      title={
        <Text variant="Table14Semibold" as="span" color={lightTheme.colors.grayScale1000}>
          {title}
        </Text>
      }
      rowsConfig={{
        headerWidth: LABEL_COLUMN_WIDTH,
        minHeight: ROW_MIN_HEIGHT,
        headerMinHeight: HEADER_ROW_MIN_HEIGHT,
      }}
      columnsConfig={columns.map(column => ({
        key: column.key,
        width: COLUMN_WIDTH,
        highlighted: column.highlighted,
        label: (
          <Text
            variant="Table14Semibold"
            as="span"
            color={column.highlighted ? accentColor : lightTheme.colors.grayScale600}
          >
            {column.label}
          </Text>
        ),
      }))}
      rows={tableRows}
      highlight={{ color: accentColor, surface: accentSurface }}
    />
  )
}

export default PlanComparisonTable
